import {
    lazyShowEditorOptions,
    lazyGetUserOptions,
    lazyOpenUpsellUrlFromSettingsDialog,
    isProofingSupportLocale,
    lazyUpdateSettingDirectly,
    lazyGetAllOverriddenOptions,
} from 'owa-proofing-option';
import { PremiumPreviewType, PremiumPreviewStatus } from '../store/schema/PremiumPreviewData';
import { SkittleCalloutStatus, ProofingViewState } from '../store/schema/ProofingViewState';
import { getUpsellSettingsStrings } from '../utils/getUpsellSettingsStrings';
import {
    EditorClient,
    UpsellEvent,
    UpsellSender,
    IUpsellContext,
    IUpsellReturnData,
    RoamingDictionaryEvent,
    IRoamingDictionaryReturnData,
    IRoamingDictionaryContext,
    FeatureListenerReaction,
    Priority,
} from '@nlapps/editor-client-web';
import { isFeatureEnabled } from 'owa-feature-flags';
import { convertPriorityToTypeString } from '../utils/convertPriorityToTypeString';
import { logUsage } from 'owa-analytics';
import { getGuid } from 'owa-guid';
import { UpsellSkittle } from '../components/UpsellSkittle';
import { logOnUpsellClicked } from '../utils/telemetry/logOnUpsellClicked';
import { getUserCulture } from 'owa-session-store';
import { trace } from 'owa-trace';
import ResizeObserver from 'resize-observer-polyfill';
import { skittleEnabled } from '../utils/skittleEnabled';
import { isProofingSupported } from 'owa-editor-proofing-common';
import { IEditor, PluginEvent, PluginEventType } from 'roosterjs-editor-types';
import { getEntitySelector } from 'roosterjs-editor-dom';
import UserOptions from 'owa-proofing-option/lib/store/schema/UserOptions';
import { shouldShowUpsell } from 'owa-proofing-option/lib/utils/shouldShowUpsell';
import SourceEntryPoint from 'owa-proofing-option/lib/store/schema/SourceEntryPoint';
import isPremium from 'owa-proofing-option/lib/utils/isPremium';
import * as SkittleMutators from '../mutators';
import { getDefaultOptionsBasedOnFlight } from '../utils/getDefaultOptionsBasedOnFlight';
import {
    DP_SKITTLE_NON_ZERO,
    PREMIUM_SUGGESTIONS_EXPIRED_COUNT,
    DP_SKITTLE_VISIBLE,
} from '../constants';
import { PluginWithUI, EditorViewState, PluginViewStateAdapter } from 'owa-editor';
import PluginUtils from 'owa-editor-plugin-utils/lib/schema/PluginUtils';
import EditorPluginEx from 'owa-editor-plugin-utils/lib/schema/EditorPluginEx';
import { lazyLogFloodgateActivity } from 'owa-floodgate-feedback-view';
import { RoamingDictionary } from '@editor/roaming-dictionary';
import { lazyGetRoamingDictionary, lazySaveRoamingDictionary } from 'owa-roaming-dictionary';
import { InfoBarHostViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { disableProofingBasedOnLength } from '../utils/disableProofingBasedOnLength';
import { getEditorBxClient, disposeEditorBxClient } from '../utils/getEditorBxClient';
import { setPremiumPreviewData } from '../utils/setPremiumPreviewData';
import { getGoPremiumUrl } from '../utils/openGoPremiumUrl';
import { enableCritiquesOnBx } from '../utils/enableCritiquesOnBx';
import { ISession } from '@augloop/runtime-client';
import { lazyGetAugLoopSession, lazyDisposeAugLoopSession, AugloopSession } from 'augloop-service';

const CREATE_UNDO_CHECKPOINT_EVENT_NAME = 'createUndoCheckpoint';
export const SHOW_EDITOR_SETTINGS_SOURCE = '_ShowEditorSettingsSource';
// The content change source for upgrading compose
const UPGRADE_CHANGE_SOURCE = 'UpgradeCompose';

// To avoid a circular dependency the class name has been copied here. This should match the value from:
// 'owa-editor-smartcompose-plugin\lib\store\schema\Suggestion.ts'
// 'owa-editor-sonora-plugin/lib/constants.ts'
const SMART_COMPOSE_SUGGESTION_SELECTOR = '.suggestedCompletion';
const SONORA_INLINE_HINT_CLASS = '.SonoraInlineHint';

/****** TELEMETRY DATAPOINTS *******/
const SESSION_START_EVENT_NAME = 'EditorProofing_SessionStart';
const SESSION_END_EVENT_NAME = 'EditorProofing_SessionEnd';
const CRITIQUE_CLICKED = 'EditorProofing_CritiqueClicked';

const FLOODGATE_SURVEY_DELAY = 1000; // 1 second

export default class ProofingPlugin implements PluginWithUI<ProofingViewState>, EditorPluginEx {
    private editor: IEditor;
    private editorClient: EditorClient;
    private nodeBeingChecked: any;
    private resizeObserver: any;
    private sessionId: string;
    private proofingOptions: UserOptions;
    private utils: PluginUtils;
    public roamingDictionary: RoamingDictionary | undefined;
    private loggedPremiumSuggestionsFound: boolean;
    public getName() {
        return 'Proofing';
    }

    constructor(
        public readonly adapter: PluginViewStateAdapter<ProofingViewState>,
        public readonly editorAdapter: PluginViewStateAdapter<EditorViewState>,
        private readonly augloopSessionAdapter: PluginViewStateAdapter<AugloopSession>,
        private composeOperation: string,
        private isCalendar: boolean,
        private infoBarViewState: InfoBarHostViewState
    ) {
        this.sessionId = getGuid();
    }

    /**
     * Initializes the plugin
     * @param editor The editor instance that is loading the plugin
     */
    public initialize(editor: IEditor) {
        this.editor = editor;
        trace.info('[ProofingPlugin] initialize: ' + this.editor);
        if (disableProofingBasedOnLength(this.editor, this.infoBarViewState)) {
            this.temporarilyDisableProofing();
        } else {
            this.initializeProofingEditorClient();
        }
    }

    public dispose() {
        const viewState = this.adapter();
        // Need to update the settings with the latest suggestions count
        if (
            viewState.premiumPreviewData.premiumPreviewType === PremiumPreviewType.CountBased &&
            this.proofingOptions.premiumSuggestionsCount !==
                viewState.premiumPreviewData.premiumSuggestionsCount
        ) {
            lazyUpdateSettingDirectly.importAndExecute(
                'premiumSuggestionsCount',
                viewState.premiumPreviewData.premiumSuggestionsCount
            );
        }

        if (this.editorClient) {
            if (this.roamingDictionary) {
                lazySaveRoamingDictionary.importAndExecute();
            }
            this.stopProofing();
            this.editorClient.shutDown();
            this.editorClient = null;
        }
        this.editor = null;
        disposeEditorBxClient(viewState.editorProofingId);
        if (isFeatureEnabled('mc-officeEditorALEnabled')) {
            lazyDisposeAugLoopSession.importAndExecute(this.augloopSessionAdapter().sessionId);
        }
    }

    public setPluginUtils(utils: PluginUtils) {
        this.utils = utils;
    }

    public onPluginEvent(e: PluginEvent) {
        if (e.eventType == PluginEventType.ContentChanged) {
            switch (e.source) {
                case UPGRADE_CHANGE_SOURCE:
                    if (disableProofingBasedOnLength(this.editor, this.infoBarViewState)) {
                        this.temporarilyDisableProofing();
                    }
                    break;
                case SHOW_EDITOR_SETTINGS_SOURCE:
                    this.showEditorSettings(e.data as Window);
                    break;
            }
        }
    }

    public getUIComponentClass() {
        return UpsellSkittle;
    }

    private showEditorSettings(targetWindow: Window) {
        const { upsellString, buttonString } = getUpsellSettingsStrings(this.adapter());
        trace.info('[ProofingPlugin] showEditorSettings');
        lazyShowEditorOptions.importAndExecute(
            targetWindow,
            this.isCalendar
                ? SourceEntryPoint.CalendarComposeToolbar
                : SourceEntryPoint.MailComposeToolbar,
            this.onUpdateSettings,
            this.onSettingsDialogButtonClicked,
            upsellString,
            buttonString
        );
    }

    private onUpdateSettings = () => {
        lazyGetUserOptions.importAndExecute(options => {
            this.proofingOptions = options;
            this.getOptionsAndUpdateEditorClient(options);
        });
    };

    private getOptionsAndUpdateEditorClient = (proofingOptions: any) => {
        const isLocaleSupported: boolean = isProofingSupportLocale(proofingOptions.proofingLocale);

        if (!isLocaleSupported) {
            this.stopProofing();
        } else {
            this.updateEditorProofingOptionsBasedOnSettings();

            // check to see if all critique types are disabled. If they are all disabled then turn off the proofing system
            if (!proofingOptions.spellCheckEnabled && !proofingOptions.grammarEnabled) {
                this.stopProofing();
            } else if (!this.nodeBeingChecked) {
                this.startProofing();
            } else {
                this.stopProofing();
                this.startProofing();
            }

            this.reProofDocument();
        }
    };

    private temporarilyDisableProofing() {
        SkittleMutators.onProofingDisable(this.adapter());
        this.editor?.setEditorDomAttribute('spellCheck', 'true');
        this.stopProofing();
    }

    private setEditorClientData(augloopSession: ISession | null) {
        // update proofingOptions so we could pass the updated language when initializing Editor Client
        lazyGetUserOptions.importAndExecute(options => {
            this.proofingOptions = options;
            if (this.editor) {
                if (skittleEnabled(options, true /* checkForSettings*/)) {
                    SkittleMutators.onShowSkittle(this.adapter());
                }
                setPremiumPreviewData(
                    options.finalDatePremiumDemo,
                    options.premiumSuggestionsCount,
                    this.adapter()
                );
                this.editorClient = getEditorBxClient(
                    this.adapter().editorProofingId,
                    this.getDefaultProofingLocale(),
                    this.composeOperation,
                    this.adapter().premiumPreviewData.premiumPreviewStatus,
                    augloopSession
                );

                this.attachUpsellCallbacks();
                this.getOptionsAndUpdateEditorClient(options);
            }
        });
    }

    /**
     * Initialize the Editor client. This is confusingly named, but the Editor is the shared Office
     * proofing service. If the mc-officeEditorBX feature flag is enabled, turn on Editor proofing for
     * this compose session.
     */
    private initializeProofingEditorClient() {
        if (!isProofingSupported()) {
            return;
        }

        // when AL flights are enabled, we need first to fetch augloop session
        // and only then to create the editor client and passing it.
        if (isFeatureEnabled('mc-officeEditorALEnabled')) {
            const sessionId = this.augloopSessionAdapter().sessionId;
            lazyGetAugLoopSession.import().then(getAugLoopSession => {
                getAugLoopSession(sessionId).then(augloopSession => {
                    if (this.editor) {
                        trace.info('[augloopSession]. Proofing plugin with Id:' + sessionId);
                        this.setEditorClientData(augloopSession);
                    }
                });
            });
        } else {
            this.setEditorClientData(null /* augloopSession */);
        }
    }

    private attachUpsellCallbacks() {
        const { premiumPreviewData } = this.adapter();

        if (skittleEnabled(undefined /* settings */, false /* checkForSettings */)) {
            this.editorClient.onPremiumCountChange((count: number, categories: string) => {
                SkittleMutators.onSkittleUpdate(this.adapter(), count, categories);
                if (!this.loggedPremiumSuggestionsFound && count > 0) {
                    logUsage(DP_SKITTLE_NON_ZERO, {}, { isCore: true });
                    this.loggedPremiumSuggestionsFound = true;
                }
            });
        }

        // callback to react when user clicks on 'try it now' within the contextual card
        // we should plug this callback only when user hasn't used the premium preview yet.
        if (premiumPreviewData.premiumPreviewStatus === PremiumPreviewStatus.New) {
            this.editorClient.onTryPremiumPreview((context: IUpsellContext) => {
                SkittleMutators.onUpdatePermiumPreviewSource(this.adapter(), 'contextualCard');
                SkittleMutators.onUpdateSkittleCallout(
                    this.adapter(),
                    SkittleCalloutStatus.ShowDialog
                );
            });
        }

        // callback to update the number of how many premium suggestions have left
        // this should be plugged only when: 1) count based experiment. 2) status is not expired
        if (
            premiumPreviewData.premiumPreviewType === PremiumPreviewType.CountBased &&
            premiumPreviewData.premiumPreviewStatus !== PremiumPreviewStatus.Expired
        ) {
            this.editorClient.onPremiumPreviewExpired(() => {
                SkittleMutators.onUpdateSkittleCallout(
                    this.adapter(),
                    SkittleCalloutStatus.ShowCallout
                );

                // update local store to re-render UI with the new count / status
                SkittleMutators.onUpdatePremiumSuggestionsData(
                    this.adapter(),
                    premiumPreviewData.numberOfDaysLeftToUsePremiunSuggestions,
                    PremiumPreviewStatus.Expired,
                    PremiumPreviewType.CountBased,
                    PREMIUM_SUGGESTIONS_EXPIRED_COUNT
                );
            });

            this.editorClient.onPreviewCritiquesCountChange((count: number) => {
                // update local store to re-render UI with the new count / status
                SkittleMutators.onUpdatePremiumSuggestionsData(
                    this.adapter(),
                    premiumPreviewData.numberOfDaysLeftToUsePremiunSuggestions,
                    PremiumPreviewStatus.Activated,
                    PremiumPreviewType.CountBased,
                    count
                );
            });
        }

        const upsellFeature = this.editorClient.upsellFeature;
        upsellFeature.ignoreDisablers = true;
        upsellFeature.addListener(UpsellEvent.CritiqueClicked, this.reactToCritiqueClicked);
        upsellFeature.addListener(UpsellEvent.UpsellButtonClicked, this.reactToUpsellClicked);

        const roamingDictionaryFeature = this.editorClient.roamingDictionaryFeature;
        roamingDictionaryFeature.addListener(
            RoamingDictionaryEvent.ClientInitialized,
            this.reactToRoamingDictionaryInitialized
        );
    }

    private onSettingsDialogButtonClicked = (): void => {
        const { premiumPreviewData } = this.adapter();
        // when status is new, the button is 'try now'
        if (premiumPreviewData.premiumPreviewStatus === PremiumPreviewStatus.New) {
            SkittleMutators.onUpdatePermiumPreviewSource(this.adapter(), 'settings');
            SkittleMutators.onUpdateSkittleCallout(this.adapter(), SkittleCalloutStatus.ShowDialog);
        } else {
            lazyOpenUpsellUrlFromSettingsDialog.importAndExecute();
            logOnUpsellClicked('SettingsCompose', premiumPreviewData, '' /* type */);
        }
    };

    private reactToRoamingDictionaryInitialized = (
        context: IRoamingDictionaryContext
    ): [FeatureListenerReaction, IRoamingDictionaryReturnData] => {
        trace.info('[roamingDictionary] reactToRoamingDictionaryInitialized');
        if (this.roamingDictionary) {
            trace.info('[roamingDictionary] reactToRoamingDictionaryInitialized: enabling feature');
            return [
                FeatureListenerReaction.EnableFeature,
                { roamingDictionary: this.roamingDictionary },
            ];
        } else {
            trace.info(
                '[roamingDictionary] reactToRoamingDictionaryInitialized: roamingDictionary not created disabling feature'
            );
            return [FeatureListenerReaction.DisableFeature, { roamingDictionary: undefined }];
        }
    };

    private reactToCritiqueClicked = (
        context: IUpsellContext
    ): [FeatureListenerReaction, IUpsellReturnData] => {
        trace.info('[ProofingPlugin] reactToCritiqueClicked');
        const { premiumPreviewData } = this.adapter();

        if (!context.critique) {
            return [FeatureListenerReaction.NoReaction, { upsellLink: undefined }];
        }

        if (context.critique.priority === Priority.Low) {
            trace.info('[ProofingPlugin] triggering floodgate survey in 1 second');
            setTimeout(() => {
                lazyLogFloodgateActivity.import().then(logFloodgateActivity => {
                    logFloodgateActivity('ProofingRefinementClicked');
                });
            }, FLOODGATE_SURVEY_DELAY);
        }
        const upsellLink = getGoPremiumUrl(
            premiumPreviewData.premiumPreviewType,
            premiumPreviewData.premiumPreviewStatus,
            'ContexualCard',
            context.critique.priority === Priority.High
        );
        logUsage(CRITIQUE_CLICKED, {
            type: convertPriorityToTypeString(context.critique.priority),
            showUpsell: shouldShowUpsell(),
        });

        return [FeatureListenerReaction.EnableFeature, { upsellLink: upsellLink }];
    };

    private reactToUpsellClicked = (
        context: IUpsellContext
    ): [FeatureListenerReaction, IUpsellReturnData] => {
        trace.info('[ProofingPlugin] reactToUpsellClicked');
        const { premiumPreviewData } = this.adapter();

        logOnUpsellClicked(
            'ContexualCard',
            premiumPreviewData,
            convertPriorityToTypeString(context.critique?.priority)
        );

        // when the sender is Outlook, it means user clicked upsell form settings and we should not pass
        // upsellLink back to the callback. This is just for logging purpuses.
        const upsellLink =
            context.sender === UpsellSender.Outlook
                ? undefined
                : getGoPremiumUrl(
                      premiumPreviewData.premiumPreviewType,
                      premiumPreviewData.premiumPreviewStatus,
                      'ContexualCard',
                      context.critique.priority === Priority.High
                  );

        return [FeatureListenerReaction.EnableFeature, { upsellLink: upsellLink }];
    };

    private async startProofing() {
        if (this.editorClient) {
            this.updateEditorProofingOptionsBasedOnSettings();
            this.logEvent(SESSION_START_EVENT_NAME);
            const viewState = this.adapter();
            const { premiumPreviewData } = this.adapter();

            if (skittleEnabled(this.proofingOptions, true /* checkForSettings */)) {
                SkittleMutators.onShowSkittle(viewState);
                logUsage(DP_SKITTLE_VISIBLE, {}, { isCore: true });
            } else {
                SkittleMutators.onHideSkittle(viewState);
            }
            // when the status is set as activated, we should enable the premium suggestions for this user
            if (premiumPreviewData.premiumPreviewStatus === PremiumPreviewStatus.Activated) {
                enableCritiquesOnBx(
                    viewState.editorProofingId,
                    premiumPreviewData,
                    premiumPreviewData.numberOfDaysLeftToUsePremiunSuggestions,
                    premiumPreviewData.premiumSuggestionsCount
                );
            }
            if (!this.roamingDictionary) {
                trace.info('[roamingDictionary] startProofing: getting roaming dictionary');

                const getRoamingDictionary = await lazyGetRoamingDictionary.import();
                this.roamingDictionary = await getRoamingDictionary();
                trace.info('[roamingDictionary]startProofing: got roaming dictionary');
            }

            // Check null for editor after async call because user can close compose during async call which can cause editor to be null
            if (this.editor) {
                // Turn off the native browser spell check for the editor
                this.editor.setEditorDomAttribute('spellCheck', 'false');
                const waterMarkCssSelector = getEntitySelector('WATERMARK_WRAPPER');
                // A CSS selector list exempt from proofing, should be passed only when SimplifiedHost is disabled:
                // SC suggestions, Sonora hints, hyper links, quotes, watermark
                const cssSelectorsToIngore: string = isFeatureEnabled(
                    'mc-officeEditorSimplifiedHostModel'
                )
                    ? ''
                    : `${SMART_COMPOSE_SUGGESTION_SELECTOR}, ${SONORA_INLINE_HINT_CLASS}, a, blockquote, ${waterMarkCssSelector}`;
                this.nodeBeingChecked = this.utils.spellCheck(
                    this.editorClient,
                    cssSelectorsToIngore
                );

                document.addEventListener(
                    CREATE_UNDO_CHECKPOINT_EVENT_NAME,
                    this.handleProofingCreateUndoCheckpointEvent
                );

                this.resizeObserver = new ResizeObserver(entries => {
                    this.reProofDocument();
                });

                this.utils.observeResize(this.resizeObserver, { box: 'border-box' });
            }
        }
    }

    private reProofDocument() {
        // Signal to the current node being checked by the Editor plugin that the document is dirty
        // and it needs to update the positions of all of the critiques.
        this.nodeBeingChecked?.children?.forEach(tileNode => {
            tileNode.reconcile(undefined, true);
        });
    }

    private updateEditorProofingOptionsBasedOnSettings = async () => {
        const {
            spellCheckEnabled,
            grammarEnabled,
            writingRefinementsEnabled,
            proofingLocale,
        } = this.proofingOptions;

        if (!this.editorClient) {
            // The editor client may have already been cleaned up by the time this callback fires
            return;
        }

        if (spellCheckEnabled) {
            this.editorClient.enableSpelling();
        } else {
            this.editorClient.disableSpelling();
        }

        if (grammarEnabled) {
            this.editorClient.enableGrammar();
        } else {
            this.editorClient.disableGrammar();
        }

        if (writingRefinementsEnabled && isPremium()) {
            this.editorClient.enableRefinements();
        } else {
            this.editorClient.disableRefinements();
        }

        const defaultOptions = await getDefaultOptionsBasedOnFlight();
        const overriddenOptions = await lazyGetAllOverriddenOptions.importAndExecute();
        if (defaultOptions) {
            this.editorClient.setAvailableCritiqueTypes(defaultOptions);
        }

        if (overriddenOptions) {
            const overriddenOptionsArray = [...overriddenOptions.values()];
            this.editorClient.setOverriddenCritiqueTypeOptions(overriddenOptionsArray);
        }

        this.editorClient.setLocale(proofingLocale);
    };

    private logEvent(eventName: string) {
        const {
            spellCheckEnabled,
            grammarEnabled,
            writingRefinementsEnabled,
        } = this.proofingOptions;

        logUsage(eventName, {
            sessionId: this.sessionId,
            spelling: spellCheckEnabled,
            grammar: grammarEnabled,
            refinements: writingRefinementsEnabled,
        });
    }

    private stopProofing() {
        if (this.editorClient && this.nodeBeingChecked) {
            this.logEvent(SESSION_END_EVENT_NAME);
            this.editor?.setEditorDomAttribute('spellCheck', 'true');
            this.editorClient.detach(this.nodeBeingChecked);
            this.nodeBeingChecked = null;
            document.removeEventListener(
                CREATE_UNDO_CHECKPOINT_EVENT_NAME,
                this.handleProofingCreateUndoCheckpointEvent
            );
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    private handleProofingCreateUndoCheckpointEvent = e => {
        this.editor.addUndoSnapshot();
    };

    private getDefaultProofingLocale(): string {
        if (this.proofingOptions?.proofingLocale) {
            return this.proofingOptions?.proofingLocale;
        } else {
            return getUserCulture();
        }
    }
}
